import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { saveAs } from 'file-saver';
import { 
  BarChart2, 
  Table, 
  MoreVertical, 
  Image, 
  FileSpreadsheet,
  RefreshCw,
  Settings,
  Trash2,
} from 'lucide-react';
import ChartRenderer from './ChartRenderer.jsx';
import DataTable from './DataTable.jsx';
import CardSkeleton from './CardSkeleton.jsx';
import ChartSettingsModal from './ChartSettingsModal.jsx';

const DashboardCard = ({
  item,
  data,
  comparisonData,
  isLoading = false,
  onRefresh,
  onEdit,
  onDelete,
  hasSettings = true, // Whether this chart has configuration settings
  chartSettings = null, // The settings from dashboard_settings API
  onSettingsSaved, // Callback when settings are saved
}) => {
  const [viewMode, setViewMode] = useState(
    item.configuration?.chart_type === 'table' || !hasSettings ? 'table' : 'chart'
  );
  const [showMenu, setShowMenu] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const chartRef = useRef(null);
  const menuRef = useRef(null);

  // Parse configuration from item (settings or API defaults)
  const mappings = useMemo(() => {
    if (!item.configuration?.mappings) return {};
    try {
      return typeof item.configuration.mappings === 'string'
        ? JSON.parse(item.configuration.mappings)
        : item.configuration.mappings;
    } catch {
      return {};
    }
  }, [item.configuration?.mappings]);

  const options = useMemo(() => {
    if (!item.configuration?.options) return { disableFilters: false };
    try {
      const parsed =
        typeof item.configuration.options === 'string'
          ? JSON.parse(item.configuration.options)
          : item.configuration.options;
      return { disableFilters: false, ...parsed };
    } catch {
      return { disableFilters: false };
    }
  }, [item.configuration?.options]);

  const chartType = item.configuration?.chart_type || (hasSettings ? 'bar' : 'table');
  const isScalar = chartType === 'scalar';
  const isTable = chartType === 'table';
  const disableFilters = options.disableFilters === true;

  const tableColumns = useMemo(() => {
    if (mappings.columns?.length) {
      return mappings.columns.map((key) => ({
        key,
        label: mappings.column_labels?.[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
        sortable: true,
        filterable: true,
      }));
    }
    return undefined;
  }, [mappings]);

  // Export to CSV
  const exportToCSV = useCallback(() => {
    if (!data || data.length === 0) return;

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map((row) =>
        headers.map((header) => {
          const value = row[header];
          // Escape values with commas or quotes
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `${item.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
    setShowMenu(false);
  }, [data, item.name]);

  // Export to PNG
  const exportToPNG = useCallback(() => {
    if (!chartRef.current) return;

    const echartsInstance = chartRef.current.getEchartsInstance();
    const url = echartsInstance.getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: '#fff',
    });

    // Convert data URL to blob and save
    fetch(url)
      .then((res) => res.blob())
      .then((blob) => {
        saveAs(blob, `${item.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.png`);
      });
    setShowMenu(false);
  }, [item.name]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showTableView = isTable || viewMode === 'table' || !hasSettings;

  const renderTable = () => (
    <DataTable
      data={data}
      columns={tableColumns}
      disableFilters={disableFilters}
    />
  );

  // Handle settings saved
  const handleSettingsSaved = (savedSettings) => {
    if (onSettingsSaved) {
      onSettingsSaved(savedSettings);
    }
    setShowSettingsModal(false);
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 h-full flex flex-col overflow-hidden hover:shadow-md transition-shadow">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-800 text-sm truncate">{item.name}</h3>
          </div>
          <div className="flex items-center gap-1">
            {!hasSettings && (
              <button
                onClick={() => setShowSettingsModal(true)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Configure Chart"
              >
                <Settings size={16} />
              </button>
            )}
            
            {hasSettings && !isScalar && !isTable && (
              <div className="flex items-center bg-gray-100 rounded-lg p-0.5 mr-2">
                <button
                  onClick={() => setViewMode('chart')}
                  className={`p-1.5 rounded-md transition-colors ${
                    viewMode === 'chart'
                      ? 'bg-white shadow-sm text-primary'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  title="Chart view"
                >
                  <BarChart2 size={16} />
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-1.5 rounded-md transition-colors ${
                    viewMode === 'table'
                      ? 'bg-white shadow-sm text-primary'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  title="Table view"
                >
                  <Table size={16} />
                </button>
              </div>
            )}

          {/* Refresh Button */}
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            </button>
          )}

          {/* More Options Menu */}
          <div ref={menuRef} className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <MoreVertical size={16} />
            </button>

            {showMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[150px]">
                {/* Configure Settings - always available */}
                <button
                  onClick={() => {
                    setShowMenu(false);
                    setShowSettingsModal(true);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Settings size={16} />
                  <span>Configure Chart</span>
                </button>
                {onEdit && (
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      onEdit();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Settings size={16} />
                    <span>Edit</span>
                  </button>
                )}
                {hasSettings && viewMode === 'chart' && !isScalar && !isTable && (
                  <button
                    onClick={exportToPNG}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Image size={16} />
                    <span>Export PNG</span>
                  </button>
                )}
                <button
                  onClick={exportToCSV}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <FileSpreadsheet size={16} />
                  <span>Export CSV</span>
                </button>
                {onDelete && (
                  <>
                    <div className="border-t border-gray-200 my-1"></div>
                    <button
                      onClick={() => {
                        setShowMenu(false);
                        onDelete();
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 size={16} />
                      <span>Delete</span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={`flex-1 min-h-0 ${showTableView && !isScalar ? 'p-2' : 'p-4'}`}>
        {isLoading ? (
          <CardSkeleton type={isScalar ? 'scalar' : showTableView ? 'table' : 'chart'} />
        ) : showTableView && !isScalar ? (
          renderTable()
        ) : (
          <ChartRenderer
            ref={chartRef}
            type={chartType}
            data={data}
            mappings={mappings}
            options={options}
            comparisonData={comparisonData}
          />
        )}
      </div>
    </div>

    {/* Settings Modal */}
    <ChartSettingsModal
      isOpen={showSettingsModal}
      onClose={() => setShowSettingsModal(false)}
      item={item}
      chartData={data}
      existingSettings={chartSettings}
      onSave={handleSettingsSaved}
    />
    </>
  );
};

export default DashboardCard;
